import React from "react";
import styles from "./lookback-and-polling-fields.module.scss";
import { DashboardEditWidgetDrawerDropdown } from "@src/components/atoms/dashboard-edit-widget-drawer-dropdown/dashboard-edit-widget-drawer-dropdown";
import {
  GAUGE_LOOKBACK_MS,
  buildPollingOptions,
  findPollingOption,
} from "@src/components/organisms/widgets/constants";
import { buildDurationOptions } from "@src/components/organisms/widgets/utils/build-duration-options";
import { findDurationOption } from "@src/components/organisms/widgets/utils/find-duration-options";

const cssClassName = "lookback-and-polling-fields";

export interface LookbackAndPollingFieldsProps {
  i18n: (key: string) => string;
  lookbackMs: number | null;
  setLookbackMs: (ms: number | null) => void;
  pollingInterval: string | null;
  setPollingInterval: (iso: string | null) => void;
}

/**
 * Shared lookback + polling cadence fields. Lifted out of the gauge's
 * inline branch so a second widget can reuse them without duplicating
 * the dropdown wiring. Carries its own `__field` rule so it renders
 * identically wherever it is mounted. Both fields honour a "null clears
 * the user pick" contract:
 * the dropdown's X button writes null, which the save flow reverts to
 * the respective default at save time.
 */
export const LookbackAndPollingFields = ({
  i18n,
  lookbackMs,
  setLookbackMs,
  pollingInterval,
  setPollingInterval,
}: LookbackAndPollingFieldsProps) => {
  const lookbackLabel = i18n("modules.lookbackAndPollingFields.lookbackLabel");
  const lookbackPlaceholder = i18n(
    "modules.lookbackAndPollingFields.lookbackPlaceholder",
  );
  const noLookbacks = i18n("modules.lookbackAndPollingFields.noLookbacks");
  const pollingLabel = i18n(
    "modules.lookbackAndPollingFields.pollingIntervalLabel",
  );
  const pollingPlaceholder = i18n(
    "modules.lookbackAndPollingFields.pollingIntervalPlaceholder",
  );
  const noPollingIntervals = i18n(
    "modules.lookbackAndPollingFields.noPollingIntervals",
  );

  return (
    <>
      <div className={styles[`${cssClassName}__field`]}>
        <DashboardEditWidgetDrawerDropdown
          options={buildDurationOptions(GAUGE_LOOKBACK_MS, i18n)}
          selectedOption={
            lookbackMs != null
              ? findDurationOption(GAUGE_LOOKBACK_MS, lookbackMs, i18n)
              : null
          }
          onSelectOption={(selected) =>
            setLookbackMs(selected ? Number(selected.source_id) : null)
          }
          labelText={lookbackLabel}
          noOptionsText={noLookbacks}
          placeholderText={lookbackPlaceholder}
          isLoading={false}
        />
      </div>

      <div className={styles[`${cssClassName}__field`]}>
        <DashboardEditWidgetDrawerDropdown
          options={buildPollingOptions(i18n)}
          selectedOption={
            pollingInterval ? findPollingOption(pollingInterval, i18n) : null
          }
          onSelectOption={(selected) =>
            setPollingInterval(selected?.source_id ?? null)
          }
          labelText={pollingLabel}
          noOptionsText={noPollingIntervals}
          placeholderText={pollingPlaceholder}
          isLoading={false}
        />
      </div>
    </>
  );
};
